/*
 * THE shared icon set. It began as the catalog surfaces' icons and now gathers
 * the glyphs the app had pasted inline per screen, path data copied verbatim so
 * nothing changes on screen. One stroke (1.75) on one grid (24), `currentColor`
 * and `aria-hidden`, so an icon never contributes to the accessible name — the
 * surrounding button or label always carries that.
 *
 * Every icon added in the gathering pass takes an optional `className`, which
 * defaults to the size its source used; the original exports keep their exact
 * signatures. `ColumnManager`'s 16-grid set is separate on purpose.
 */

const stroke = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

export function SearchIcon() {
  return (
    <svg {...stroke} className="h-4 w-4">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

export function PlusIcon() {
  return (
    <svg {...stroke} className="h-4 w-4">
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}

export function PencilIcon() {
  return (
    <svg {...stroke} className="h-4 w-4">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

export function TrashIcon() {
  return (
    <svg {...stroke} className="h-4 w-4">
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}

/**
 * A pen, mid-signature. Button-sized, for "send for signature" controls.
 *
 * The same two paths as the glyph inside `DocuSignCard`, which is that card's
 * private `h-5 w-5` empty-state variant. Kept identical on purpose so the two
 * read as one idea at two sizes; the card's copy is not exported and was left
 * alone because that file had uncommitted work from another session.
 */
export function SignatureIcon() {
  return (
    <svg {...stroke} className="h-4 w-4">
      <path d="M3 19c3.5 0 3.5-11 6.5-11S13 17 16 17c1.6 0 2.5-1 3-2" />
      <path d="M3 21h18" />
    </svg>
  );
}

const CHEVRON_PATH = {
  left: 'm15 18-6-6 6-6',
  right: 'm9 18 6-6-6-6',
  down: 'm6 9 6 6 6-6',
  up: 'm18 15-6-6-6 6',
} as const;

export function ChevronIcon({
  direction,
  className = 'h-3.5 w-3.5',
}: {
  direction: 'left' | 'right' | 'up' | 'down';
  className?: string;
}) {
  return (
    <svg {...stroke} className={className}>
      <path d={CHEVRON_PATH[direction]} />
    </svg>
  );
}

export function AlertIcon() {
  return (
    <svg {...stroke} className="mt-px h-4 w-4 shrink-0">
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
    </svg>
  );
}

/** Empty-state glyph for products. */
export function TagIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <path d="M3 8.5V4a1 1 0 0 1 1-1h4.5a2 2 0 0 1 1.42.59l9.5 9.5a2 2 0 0 1 0 2.82l-4.5 4.5a2 2 0 0 1-2.82 0l-9.5-9.5A2 2 0 0 1 3 8.5Z" />
      <circle cx="7.5" cy="7.5" r="1.25" />
    </svg>
  );
}

/** Empty-state glyph for cycles. */
export function CycleIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1-8.06-5" />
      <path d="M3 12a9 9 0 0 1 9-9 9 9 0 0 1 8.06 5" />
      <path d="M20 3v5h-5" />
      <path d="M4 21v-5h5" />
    </svg>
  );
}

/** Empty-state glyph for intakes. */
export function IntakeIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <path d="M3 12h4l2 3h6l2-3h4" />
      <path d="M5.5 5h13a2 2 0 0 1 1.94 1.5L22 12v5a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-5l1.56-5.5A2 2 0 0 1 5.5 5Z" />
    </svg>
  );
}

/* ── Gathered from the app (decision 10): same paths, same look ─────────────── */

type IconProps = { className?: string };

/** A tick. From `AppShell` / `TaskList`. */
export function CheckIcon({ className = 'h-3.5 w-3.5 shrink-0' }: IconProps) {
  return (
    <svg {...stroke} className={className}>
      <path d="m5 12 5 5L20 7" />
    </svg>
  );
}

/** A cross, for dismiss / close. From `EnquiryAlerts` / `GuideDrawer`. */
export function CloseIcon({ className = 'h-4 w-4 shrink-0' }: IconProps) {
  return (
    <svg {...stroke} className={className}>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

/** An envelope. From `CompanyDetailPage` / `ContactDetailPage`. */
export function MailIcon({ className = 'h-3.5 w-3.5' }: IconProps) {
  return (
    <svg {...stroke} className={className}>
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}

/** A handset. From `CompanyDetailPage`. */
export function PhoneIcon({ className = 'h-3.5 w-3.5' }: IconProps) {
  return (
    <svg {...stroke} className={className}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z" />
    </svg>
  );
}

/** Two people (Lucide `users`). From `CompaniesPage` / the UP dashboard's contacts button. */
export function UsersIcon({ className = 'h-4 w-4 shrink-0' }: IconProps) {
  return (
    <svg {...stroke} className={className}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

/** Show password. From `LoginPage` / `UserFormModal` / `OnboardingEmailModal`. */
export function EyeIcon({ className = 'h-4 w-4' }: IconProps) {
  return (
    <svg {...stroke} className={className}>
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/** Hide password. Same three sources as `EyeIcon`. */
export function EyeOffIcon({ className = 'h-4 w-4' }: IconProps) {
  return (
    <svg {...stroke} className={className}>
      <path d="M10.7 5.1A10.6 10.6 0 0 1 12 5c6.4 0 10 7 10 7a17.6 17.6 0 0 1-2.8 3.8" />
      <path d="M6.6 6.6A17.4 17.4 0 0 0 2 12s3.6 7 10 7a10.4 10.4 0 0 0 5.4-1.5" />
      <path d="M14.1 14.1a3 3 0 1 1-4.2-4.2" />
      <path d="m2 2 20 20" />
    </svg>
  );
}

/** Leaves the app (box + arrow). From `CompanyDetailPage` / `XeroPanel`. */
export function ExternalLinkIcon({ className = 'h-3.5 w-3.5' }: IconProps) {
  return (
    <svg {...stroke} className={className}>
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </svg>
  );
}

/** "Open this record" ↗. From `CompaniesPage` / `ProjectInlineCells`. */
export function ArrowUpRightIcon({ className = 'h-3.5 w-3.5' }: IconProps) {
  return (
    <svg {...stroke} className={className}>
      <path d="M7 17 17 7" />
      <path d="M7 7h10v10" />
    </svg>
  );
}

/** "No price on this line" ⊘. From the quote, invoice, deal and credit-note grids. */
export function NoPriceIcon({ className = 'h-3.5 w-3.5' }: IconProps) {
  return (
    <svg {...stroke} className={className}>
      <circle cx="12" cy="12" r="8" />
      <path d="m6.5 6.5 11 11" />
    </svg>
  );
}

/** An inbox tray. From `EnquiryAlerts`. */
export function InboxIcon({ className = 'h-6 w-6 shrink-0' }: IconProps) {
  return (
    <svg {...stroke} className={className}>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  );
}
